import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GuestForm, defaultGuests, emptyGuest, applyScan } from "@/components/GuestForm";
import { api, errMsg, errList, fmtDate } from "@/lib/api";
import { tr, LANGS } from "@/lib/i18n";

const HERO = "https://images.unsplash.com/photo-1659204236580-1c481bd45adf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function PublicCheckin() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [lang, setLang] = useState("it");
  const [guests, setGuests] = useState([]);
  const [docs, setDocs] = useState([]);
  const [gdpr, setGdpr] = useState(false);
  const [errors, setErrors] = useState([]);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const t = tr(lang);
  useEffect(() => {
    api.get(`/public/checkin/${token}`).then(({ data }) => {
      setInfo(data);
      setLang(data.lang || "it");
      setGuests(data.guests.length ? data.guests : defaultGuests(data.guests_count));
    }).catch(() => setFailed(true));
    api.get("/lookup/documenti").then(({ data }) => setDocs(data));
  }, [token]);

  const onScan = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/public/checkin/${token}/document`, fd);
      setGuests((gs) => gs.map((g, j) => (j === i ? applyScan(g, data) : g)));
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };

  const submit = async () => {
    setErrors([]);
    setSending(true);
    try {
      await api.post(`/public/checkin/${token}`, { guests, gdpr_accepted: gdpr, lang });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      const list = errList(e);
      setErrors(list.length ? list : [errMsg(e)]);
      toast.error(t("required"));
    }
    setSending(false);
  };

  if (failed) return <div className="min-h-screen grid place-items-center p-6 text-center text-stone-600" data-testid="checkin-invalid">{t("expired")}</div>;
  if (!info) return <div className="min-h-screen grid place-items-center text-stone-500">…</div>;
  return (
    <div className="min-h-screen bg-background" data-testid="public-checkin-page">
      <header className="relative h-72 sm:h-80 overflow-hidden grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-stone-900/10" />
        <img src="/logo-dimori.svg" alt="Dimori Guest House" className="absolute top-4 left-4 sm:left-10 h-12 w-12 sm:h-16 sm:w-16 rounded-xl shadow-lg" data-testid="checkin-logo" />
        <div className="absolute top-4 right-4 w-36">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="bg-white/90 backdrop-blur" data-testid="checkin-lang-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map(([c, l]) => <SelectItem key={c} value={c} data-testid={`checkin-lang-${c}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="absolute bottom-6 left-4 sm:left-10 right-4 text-white">
          <p className="text-xs uppercase tracking-widest text-white/70">{t("title")}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold" data-testid="checkin-property-name">{info.property.name}</h1>
          <p className="text-white/80 mt-1 text-sm">{t("stay")}: {fmtDate(info.arrival)} → {fmtDate(info.departure)} · {t("room")}: {info.room_name}</p>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {done ? (
          <div className="rounded-2xl border bg-white p-8 text-center fade-up" data-testid="checkin-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h2 className="font-display text-3xl font-extrabold mt-4">{t("sent")}</h2>
            <p className="text-stone-500 mt-2">{t("sentSub")}</p>
          </div>
        ) : (
          <>
            {info.locked && <p className="flex items-center gap-2 rounded-xl bg-emerald-50 p-4 text-emerald-800" data-testid="checkin-locked"><Lock className="h-4 w-4" />{t("locked")}</p>}
            <GuestForm guests={guests} setGuests={setGuests} t={t} docs={docs} onScan={onScan} readOnly={info.locked} />
            {!info.locked && (
              <>
                <Button variant="outline" onClick={() => setGuests([...guests, emptyGuest(guests.some((g) => g.tipo === "18") ? "20" : "19")])} data-testid="checkin-add-guest-button"><Plus className="h-4 w-4 mr-2" />{t("addGuest")}</Button>
                <div className="rounded-2xl border bg-white p-5 space-y-3" data-testid="gdpr-section">
                  <h3 className="font-display text-lg font-bold">{t("gdprTitle")}</h3>
                  <p className="text-sm text-stone-600 max-h-40 overflow-y-auto leading-relaxed">{t("gdprText")}</p>
                  <label className="flex items-start gap-3 text-sm font-medium">
                    <Checkbox checked={gdpr} onCheckedChange={(v) => setGdpr(!!v)} data-testid="gdpr-checkbox" />{t("gdprAccept")}
                  </label>
                </div>
                {errors.length > 0 && <ul className="rounded-xl border border-rose-200 bg-rose-50 p-4 pl-8 list-disc text-sm text-rose-800" data-testid="checkin-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
                <Button size="lg" className="w-full h-12 text-base" disabled={!gdpr || sending} onClick={submit} data-testid="checkin-submit-button">{t("submit")}</Button>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
